import React, { useMemo, useState } from 'react';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import ProgrammeService from '../services/ProgrammeService';
import ActivityService from '../services/ActivityService';
import { useStudentPortal } from '../store/StudentPortalContext';
import { formatDate } from '../utils/dateFormat';
import { useNavigate } from 'react-router-dom';

export default function StudentPortalTimetablePage() {
  const { student } = useStudentPortal();
  const navigate = useNavigate();
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const activityService = useMemo(() => new ActivityService(), []);
  const [activeView, setActiveView] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const programmes = useMemo(() => programmeService.list(), [programmeService]);
  const activities = useMemo(() => activityService.list(), [activityService]);

  const enrolledProgrammes = useMemo(() => {
    if (!student) return [];
    const programmeNames = new Set((student.enrolments || []).map((enrol) => enrol.programme));
    return programmes.filter((programme) => programmeNames.has(programme.name));
  }, [programmes, student]);

  const enrolledActivities = useMemo(() => {
    if (!student) return [];
    const activityIds = new Set((student.activities || []).map((activity) => activity.activityId || activity.id));
    return activities.filter((activity) => activityIds.has(activity.id));
  }, [activities, student]);

  const nextActivity = useMemo(() => {
    const upcoming = enrolledActivities
      .filter((activity) => activity.date)
      .map((activity) => ({ ...activity, dateObj: new Date(activity.date) }))
      .filter((activity) => !Number.isNaN(activity.dateObj.getTime()))
      .sort((a, b) => a.dateObj - b.dateObj);
    return upcoming[0] || null;
  }, [enrolledActivities]);

  const filteredProgrammes = useMemo(() => {
    if (!searchQuery) return enrolledProgrammes;
    const query = searchQuery.toLowerCase();
    return enrolledProgrammes.filter((programme) => programme.name?.toLowerCase().includes(query));
  }, [enrolledProgrammes, searchQuery]);

  const filteredActivities = useMemo(() => {
    if (!searchQuery) return enrolledActivities;
    const query = searchQuery.toLowerCase();
    return enrolledActivities.filter((activity) => activity.name?.toLowerCase().includes(query));
  }, [enrolledActivities, searchQuery]);

  const openDetail = (item, type) => {
    if (!item?.id) return;
    navigate(`/student-portal/timetable/${type}/${item.id}`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Timetable</h2>
        <p className="sp-muted">Your scheduled programmes, activities, and lesson plans.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-xs sp-muted">Programmes</div>
          <div className="sp-value" style={{ fontWeight: 600, fontSize: '20px' }}>{enrolledProgrammes.length}</div>
          <div className="text-xs sp-muted mt-1">Weekly learning sessions</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs sp-muted">Activities</div>
          <div className="sp-value" style={{ fontWeight: 600, fontSize: '20px' }}>{enrolledActivities.length}</div>
          <div className="text-xs sp-muted mt-1">Trips and events</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs sp-muted">Next activity</div>
          <div className="sp-value" style={{ fontWeight: 600, fontSize: '16px' }}>
            {nextActivity ? nextActivity.name : 'No upcoming date'}
          </div>
          <div className="text-xs sp-muted mt-1">
            {nextActivity?.date ? formatDate(nextActivity.date) : 'Check back later'}
          </div>
        </Card>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {['all', 'programmes', 'activities'].map((view) => (
            <Button
              key={view}
              size="sm"
              variant={activeView === view ? 'secondary' : 'ghost'}
              onClick={() => setActiveView(view)}
            >
              {view.charAt(0).toUpperCase() + view.slice(1)}
            </Button>
          ))}
        </div>
        <input
          className="sp-input w-full md:w-72"
          placeholder="Search programmes or activities"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {(activeView === 'all' || activeView === 'programmes') && (
          <Card className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Programmes</h3>
              <Badge variant="inactive">{filteredProgrammes.length} total</Badge>
            </div>
            {filteredProgrammes.length === 0 ? (
              <p className="text-sm sp-muted">No programmes assigned yet.</p>
            ) : (
              <div className="space-y-3">
                {filteredProgrammes.map((programme) => (
                  <button
                    key={programme.id}
                    type="button"
                    className="sp-panel sp-panel--muted p-4 text-left w-full hover:shadow-sm transition"
                    onClick={() => openDetail(programme, 'programme')}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>{programme.name}</div>
                        <div className="text-xs sp-muted">
                          {programme.day || programme.days?.join(', ') || 'TBD'} · {programme.time || 'TBD'}
                        </div>
                      </div>
                      <Badge variant="active">Weekly</Badge>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs sp-muted flex-wrap">
                      <span>Lesson plans: {programme.lessonPlans?.length || 0}</span>
                      <span>Documents: {programme.documents?.length || 0}</span>
                      {programme.staff && <span>Staff: {programme.staff}</span>}
                    </div>
                    <div className="mt-2 text-xs sp-muted">Click to view full course details.</div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        )}

        {(activeView === 'all' || activeView === 'activities') && (
          <Card className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Activities</h3>
              <Badge variant="inactive">{filteredActivities.length} total</Badge>
            </div>
            {filteredActivities.length === 0 ? (
              <p className="text-sm sp-muted">No activities scheduled yet.</p>
            ) : (
              <div className="space-y-3">
                {filteredActivities.map((activity) => (
                  <button
                    key={activity.id}
                    type="button"
                    className="sp-panel sp-panel--muted p-4 text-left w-full hover:shadow-sm transition"
                    onClick={() => openDetail(activity, 'activity')}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>{activity.name}</div>
                        <div className="text-xs sp-muted">
                          {formatDate(activity.date)} · {activity.time || 'TBD'}
                        </div>
                      </div>
                      <Badge variant="review">{activity.status || 'Scheduled'}</Badge>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs sp-muted flex-wrap">
                      <span>Lesson plans: {activity.lessonPlans?.length || 0}</span>
                      <span>Documents: {activity.documents?.length || 0}</span>
                      {activity.location && <span>Location: {activity.location}</span>}
                    </div>
                    <div className="mt-2 text-xs sp-muted">Click to view full activity details.</div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
